import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import { Draggable } from 'react-beautiful-dnd';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';

function LevelItem({ level, index, isLoading, onRemove, onInput, setHasError }) {
  const [isEditing, setIsEditing] = useState(level.isNew || false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!level.title) {
      setHasError(true);
    }
  }, [level.title, setHasError]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  return (
    <Draggable isDragDisabled={isLoading} draggableId={level._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(
            'flex items-center gap-3 p-3 rounded-lg border transition-all',
            snapshot.isDragging ? 'bg-muted shadow-lg' : 'bg-card hover:bg-muted/50',
            !level.title && 'border-destructive',
          )}
          style={{
            ...provided.draggableProps.style,
            borderColor: !level.title ? undefined : 'hsl(var(--border))',
          }}
        >
          {/* Drag Handle */}
          <div
            {...provided.dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          >
            <GripVertical className="h-5 w-5" />
          </div>

          {/* Level Key Badge */}
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm">
            {level.key}
          </div>

          {/* Level Title */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={level.title}
                onChange={(e) => onInput(e.target.value, index)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="Enter level title..."
                className={cn(
                  'w-full px-3 py-1.5 rounded-md text-sm',
                  'bg-background border border-input',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
                  'placeholder:text-muted-foreground',
                  !level.title && 'border-destructive focus:ring-destructive',
                )}
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className={cn(
                  'w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors',
                  'hover:bg-muted',
                  level.title ? 'text-foreground' : 'text-destructive italic',
                )}
              >
                {level.title || 'No title - click to edit'}
              </button>
            )}
          </div>

          {/* Actions */}
          <div
            className={cn(
              'flex items-center gap-1 transition-opacity',
              isHovered || isEditing ? 'opacity-100' : 'opacity-0',
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              title="Edit title"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {level.isNew && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                disabled={isLoading}
                title="Remove level"
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

LevelItem.propTypes = {
  level: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    isNew: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  onInput: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
};

export default LevelItem;
